/**
 * v5.10 A4 — Custom Reports Tab.
 * Measure × Dimension × Chart × Period × Scope live builder,
 * with Save View and Schedule.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTerm } from "@/lib/terminology.jsx";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { BarChart3, Save, Calendar, Trash2, Play, ChevronLeft, ChevronRight } from "lucide-react";
import ReportsSubNav from "@/components/ReportsSubNav";

const PERIODS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "custom", label: "Custom" },
];

export default function CustomReports() {
  const term = useTerm();
  const { pipelines, pipelineId } = usePipelineCtx();
  const [dims, setDims] = useState([]);
  const [saved, setSaved] = useState([]);
  const [config, setConfig] = useState({
    name: "",
    measure: "deal_count",
    group_by_dimension: "current_stage_id",
    chart_type: "bar",
    date_range_type: "month",
    date_range_start: null,
    date_range_end: null,
    pipeline_scope: "all_pipelines",
    scoped_pipeline_id: null,
  });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    api.get("/reports/dimensions", { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setDims(data)).catch(() => {});
    api.get("/report-views").then(({ data }) => setSaved(data)).catch(() => {});
  }, [pipelineId]);

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reports/custom", config);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally { setRunning(false); }
  }, [config]);

  // Auto-run on config change
  useEffect(() => {
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [run]);

  const saveView = async () => {
    if (!config.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) {
        await api.patch(`/report-views/${editingId}`, config);
        toast.success("View updated");
      } else {
        await api.post("/report-views", config);
        toast.success("View saved");
      }
      const { data } = await api.get("/report-views");
      setSaved(data);
      setEditingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const loadView = (v) => {
    setEditingId(v.view_id);
    setConfig({
      name: v.name, measure: v.measure,
      group_by_dimension: v.group_by_dimension, chart_type: v.chart_type,
      date_range_type: v.date_range_type,
      date_range_start: v.date_range_start,
      date_range_end: v.date_range_end,
      pipeline_scope: v.pipeline_scope,
      scoped_pipeline_id: v.scoped_pipeline_id,
    });
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete view "${v.name}"?${v.is_scheduled ? " This also cancels its schedule." : ""}`)) return;
    try {
      await api.delete(`/report-views/${v.view_id}`);
      setSaved((s) => s.filter((x) => x.view_id !== v.view_id));
      toast.success("Deleted");
    } catch (e) { toast.error("Failed"); }
  };

  const download = (fmt) => {
    if (!result) return;
    if (fmt === "csv") {
      const rows = [["label", "value"], ...result.rows.map((r) => [r.label, r.value])];
      const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${config.name || "report"}.csv`;
      a.click();
    }
  };

  const border = { borderColor: "var(--cw-border)" };
  return (
    <div className="p-8">
      <ReportsSubNav />
      <div className="cw-caption uppercase tracking-widest mt-6">Custom Reports</div>
      <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
        <BarChart3 size={24} /> Custom Report Builder
      </h1>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        Live-preview report configurations. Save, schedule, and share as needed.
      </p>

      <div className="mt-6 grid lg:grid-cols-[380px,1fr] gap-6">
        {/* LEFT: Config panel */}
        <div className="cw-card p-5 space-y-4" data-testid="report-config">
          <Field label="View name">
            <input value={config.name}
              onChange={(e) => setConfig({ ...config, name: e.target.value })}
              placeholder="Q1 Deals by Sector"
              className="w-full h-9 px-3 rounded-md border text-sm" style={border}
              data-testid="report-name" />
          </Field>

          <Field label="Measure">
            <select value={config.measure}
              onChange={(e) => setConfig({ ...config, measure: e.target.value })}
              className="w-full h-9 px-3 rounded-md border text-sm bg-white" style={border}
              data-testid="report-measure">
              <option value="deal_count">{term("Deal")} count</option>
              <option value="round_size_total">Total round size</option>
            </select>
          </Field>

          <Field label="Group by">
            <select value={config.group_by_dimension}
              onChange={(e) => setConfig({ ...config, group_by_dimension: e.target.value })}
              className="w-full h-9 px-3 rounded-md border text-sm bg-white" style={border}
              data-testid="report-dimension">
              {dims.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Chart type">
            <div className="grid grid-cols-4 gap-1">
              {["bar", "pie", "line", "funnel"].map((c) => (
                <button key={c}
                  onClick={() => setConfig({ ...config, chart_type: c })}
                  className={`h-9 rounded-md border text-xs capitalize ${
                    config.chart_type === c ? "font-semibold" : "text-[var(--cw-muted)]"}`}
                  style={config.chart_type === c ? { ...border, borderColor: "var(--cw-primary)", background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" } : border}
                  data-testid={`chart-${c}`}>{c}</button>
              ))}
            </div>
          </Field>

          <Field label="Period">
            <div className="grid grid-cols-4 gap-1">
              {PERIODS.map((p) => (
                <button key={p.key}
                  onClick={() => setConfig({ ...config, date_range_type: p.key })}
                  className={`h-9 rounded-md border text-xs ${config.date_range_type === p.key ? "font-semibold" : "text-[var(--cw-muted)]"}`}
                  style={config.date_range_type === p.key
                    ? { ...border, borderColor: "var(--cw-primary)", background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }
                    : border}
                  data-testid={`period-${p.key}`}>
                  {p.label}
                </button>
              ))}
            </div>
            {config.date_range_type === "custom" && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input type="date" value={config.date_range_start || ""}
                  onChange={(e) => setConfig({ ...config, date_range_start: e.target.value })}
                  className="w-full h-9 px-2 rounded-md border text-xs" style={border} />
                <input type="date" value={config.date_range_end || ""}
                  onChange={(e) => setConfig({ ...config, date_range_end: e.target.value })}
                  className="w-full h-9 px-2 rounded-md border text-xs" style={border} />
              </div>
            )}
          </Field>

          <Field label="Scope">
            <div className="grid grid-cols-2 gap-1">
              {[
                { k: "this_pipeline", l: `This ${term("pipeline").toLowerCase()}` },
                { k: "all_pipelines", l: `All ${term("pipelines").toLowerCase()}` },
              ].map((s) => (
                <button key={s.k}
                  onClick={() => setConfig({ ...config, pipeline_scope: s.k,
                    scoped_pipeline_id: s.k === "this_pipeline" ? pipelineId : null })}
                  className={`h-9 rounded-md border text-xs ${config.pipeline_scope === s.k ? "font-semibold" : "text-[var(--cw-muted)]"}`}
                  style={config.pipeline_scope === s.k
                    ? { ...border, borderColor: "var(--cw-primary)", background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }
                    : border}
                  data-testid={`scope-${s.k}`}>
                  {s.l}
                </button>
              ))}
            </div>
          </Field>

          <div className="flex gap-2 pt-2">
            <button onClick={saveView}
              className="cw-btn-primary text-sm inline-flex items-center gap-1 flex-1"
              data-testid="save-view-btn">
              <Save size={14} /> {editingId ? "Update view" : "Save view"}
            </button>
            <button onClick={() => download("csv")} disabled={!result}
              className="cw-btn-ghost text-sm">
              CSV
            </button>
          </div>
        </div>

        {/* RIGHT: Preview + saved list */}
        <div className="space-y-6">
          <div className="cw-card p-5" data-testid="report-preview">
            <div className="flex items-center justify-between mb-3">
              <h3 className="cw-h3">Preview</h3>
              {result && (
                <div className="text-xs text-[var(--cw-muted)]">
                  {result.period_start.slice(0, 10)} → {result.period_end.slice(0, 10)} · Total: {result.total}
                </div>
              )}
            </div>
            {running && <div className="text-xs text-[var(--cw-muted)]">Computing…</div>}
            {result && !running && (
              <BarPreview rows={result.rows} />
            )}
          </div>

          <div className="cw-card p-5" data-testid="saved-views">
            <h3 className="cw-h3 mb-3">Saved views</h3>
            {saved.length === 0 ? (
              <div className="text-xs text-[var(--cw-muted)] italic">
                No saved views yet.
              </div>
            ) : (
              <div className="space-y-2">
                {saved.map((v) => (
                  <SavedRow key={v.view_id} view={v}
                    onOpen={() => loadView(v)}
                    onDelete={() => deleteView(v)}
                    onScheduleUpdated={async () => {
                      const { data } = await api.get("/report-views");
                      setSaved(data);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--cw-muted)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function BarPreview({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-[var(--cw-muted)] italic">No data in this period.</div>;
  }
  const max = Math.max(...rows.map((r) => r.value || 0), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-40 text-xs truncate">{r.label}</div>
          <div className="flex-1 h-6 rounded-md relative overflow-hidden"
            style={{ background: "rgba(40,42,116,0.06)" }}>
            <div className="h-full" style={{
              width: `${(r.value / max) * 100}%`,
              background: "linear-gradient(90deg, var(--cw-primary), var(--cw-secondary))",
            }} />
          </div>
          <div className="w-14 text-right text-xs font-semibold">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function SavedRow({ view, onOpen, onDelete, onScheduleUpdated }) {
  const [schEditing, setSchEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    is_scheduled: !!view.is_scheduled,
    schedule_day_of_week: view.schedule_day_of_week ?? 0,
    schedule_time: view.schedule_time || "09:00",
    recipient_user_ids: view.recipient_user_ids || [],
  });
  useEffect(() => {
    if (schEditing && users.length === 0) {
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [schEditing, users.length]);

  const save = async () => {
    try {
      await api.post(`/report-views/${view.view_id}/schedule`, form);
      toast.success("Schedule updated");
      setSchEditing(false);
      onScheduleUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="rounded-md border p-3" style={{ borderColor: "var(--cw-border)" }}
      data-testid={`saved-view-${view.view_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-sm">{view.name}</div>
          <div className="text-[10px] uppercase text-[var(--cw-muted)] mt-0.5">
            {view.measure} · {view.group_by_dimension} · {view.date_range_type}
          </div>
          {view.is_scheduled && (
            <div className="text-[10px] mt-0.5" style={{ color: "var(--cw-primary)" }}>
              Scheduled · day {view.schedule_day_of_week} @ {view.schedule_time} · {(view.recipient_user_ids || []).length} recipients
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={onOpen} className="cw-btn-ghost text-xs">
            <Play size={12} className="inline mr-1" /> Open
          </button>
          <button onClick={() => setSchEditing((v) => !v)}
            className="cw-btn-ghost text-xs">
            <Calendar size={12} className="inline mr-1" />
            {view.is_scheduled ? "Edit schedule" : "Schedule"}
          </button>
          <button onClick={onDelete} className="text-xs text-[var(--cw-error)]">
            <Trash2 size={12} className="inline mr-1" /> Delete
          </button>
        </div>
      </div>
      {schEditing && (
        <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: "var(--cw-border)" }}>
          <label className="text-xs inline-flex items-center gap-1">
            <input type="checkbox" checked={form.is_scheduled}
              onChange={(e) => setForm({ ...form, is_scheduled: e.target.checked })} />
            Enable schedule
          </label>
          {form.is_scheduled && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <select value={form.schedule_day_of_week}
                  onChange={(e) => setForm({ ...form, schedule_day_of_week: Number(e.target.value) })}
                  className="h-9 px-2 rounded-md border text-xs bg-white"
                  style={{ borderColor: "var(--cw-border)" }}>
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                    <option key={i} value={i}>{d}</option>
                  ))}
                </select>
                <input type="time" value={form.schedule_time}
                  onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
                  className="h-9 px-2 rounded-md border text-xs" style={{ borderColor: "var(--cw-border)" }} />
              </div>
              <select multiple value={form.recipient_user_ids}
                onChange={(e) => setForm({ ...form, recipient_user_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                className="w-full min-h-[80px] px-2 py-1 rounded-md border text-xs bg-white"
                style={{ borderColor: "var(--cw-border)" }}>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.full_name} ({u.email})</option>
                ))}
              </select>
              <div className="text-[10px] text-[var(--cw-muted)]">
                Ctrl/⌘-click to select multiple. Notified in-app only.
              </div>
            </>
          )}
          <button onClick={save} className="cw-btn-primary text-xs">Save schedule</button>
        </div>
      )}
    </div>
  );
}
